import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { ArrowDown, ArrowLeft } from "lucide-react";
import { redJodi } from "../../Charts/JodiCharts/Chart.config";
import { fa_time } from "../../Helpers/getWeekDays";
import {
  GET_ALL_JACKPOT_JODI_CHART,
  GET_ALL_STARLINE_PANA_CHART,
} from "../../service/admin.service";

import { AllJackPotChart } from "../../../components/Charts/ShreeJackpot/AllJackpotCharts";
import { ALllStarlineCharts } from "../../../components/Charts/starLinePanaChart/AllStarlineCharts";

/* ---------- Skeleton Row ---------- */
const SkeletonRow = ({ colCount }) => (
  <tr>
    {Array.from({ length: colCount }).map((_, i) => (
      <td key={i} className="tw-border tw-p-2">
        <div className="tw-animate-pulse tw-h-6 tw-bg-gray-200 tw-rounded" />
      </td>
    ))}
  </tr>
);

/* ---------- Reusable Row Component ---------- */
const ChartRow = ({ item, isJackpot, isStarline }) => (
  <tr>
    <td className="tw-border tw-font-semibold tw-p-2">
      {fa_time(item.resultDate)}
    </td>
    {item.results.map((nestedItem, index) => {
      const digit = nestedItem?.winningDigit || "";
      const family = nestedItem?.winningDigitFamily ?? "";

      const isRedJodi =
        isJackpot &&
        digit &&
        redJodi.map((j) => parseInt(j)).includes(parseInt(digit));

      const isTriple =
        isStarline &&
        digit.length === 3 &&
        digit[0] === digit[1] &&
        digit[1] === digit[2];

      const isRed = isRedJodi || isTriple;

      return (
        <td key={index} className="tw-border tw-p-2 tw-font-bold">
          <div className="tw-flex tw-flex-col tw-items-center">
            <span className={isRed ? "tw-text-red-600" : "tw-text-black"}>
              {digit}
            </span>
            <span
              className={
                isRed
                  ? "tw-text-red-600 tw-text-[16px] tw-font-bold"
                  : "tw-text-black tw-text-[16px] tw-font-bold"
              }
            >
              {family}
            </span>
          </div>
        </td>
      );
    })}
  </tr>
);

const AllCharts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);

  /* detect chart type */
  const isJackpot = location.pathname.includes("jackpot");
  const isStarline = location.pathname.includes("starline");

  // ✅ Route ke hisaab se sahi static data select karo
  const staticData = isJackpot
    ? AllJackPotChart
    : isStarline
      ? ALllStarlineCharts
      : [];

  const starlineItems = [
    "10 AM",
    "11 AM",
    "12 PM",
    "1 PM",
    "2 PM",
    "3 PM",
    "4 PM",
    "5 PM",
    "6 PM",
    "7 PM",
    "8 PM",
    "9 PM",
  ];

  const jackpotItems = [
    "10.30 AM",
    "11.30 AM",
    "12.30 PM",
    "1.30 PM",
    "2.30 PM",
    "3.30 PM",
    "4.30 PM",
    "5.30 PM",
    "6.30 PM",
    "7.30 PM",
    "8.30 PM",
    "9.30 PM",
  ];

  const staticTimeSlot = isJackpot
    ? jackpotItems
    : isStarline
      ? starlineItems
      : [];

  /* ---------- API data fetch ---------- */
  useEffect(() => {
    const loadCharts = async () => {
      try {
        setLoading(true);

        let res;

        if (isJackpot) {
          setTitle("JACKPOT CHART");
          res = await GET_ALL_JACKPOT_JODI_CHART();
        }

        if (isStarline) {
          setTitle("STARLINE CHART");
          res = await GET_ALL_STARLINE_PANA_CHART();
        }

        console.log("API DATA", res);

        if (Array.isArray(res?.data)) {
          setData(res.data);
        } else {
          setData([]);
        }
      } catch (err) {
        console.log(err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    loadCharts();
  }, [location.pathname]);

  /* ---------- scroll to top on mount ---------- */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleClick = () => {
    const targetElement = document.getElementById("scroll-down");
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  // ✅ Column headers — pehle static se, warna dynamic se

  const timeSlots =
    staticTimeSlot.map((r) => r.trim()) ||
    data?.[0]?.results?.map((r) => r.providerName?.trim()) ||
    [];

  return (
    <>
      <div id="scroll-up"></div>
      <Navbar />

      <div className="tw-pt-8 tw-px-0 md:tw-px-8 lg:tw-px-16 tw-min-h-screen lg:tw-mt-20 tw-mt-12">
        <div className="tw-max-w-6xl tw-mx-auto">
          {/* Header */}
          <div className="tw-text-center tw-px-4 md:tw-px-8 tw-mb-8">
            <h1 className="tw-text-2xl md:tw-text-3xl lg:tw-text-4xl tw-font-bold tw-text-gray-900 tw-mb-3">
              ALL {title}
            </h1>
            <p className="tw-text-gray-600 tw-text-xs md:tw-text-sm tw-max-w-3xl tw-mx-auto tw-mb-6">
              ALL {title} Pana Chart lets you easily check old records, result
              history, bracket details, live updates, and complete digit numbers
              for better tracking.
            </p>
            <button
              onClick={handleClick}
              className="tw-bg-[#1e6ebe] tw-text-white tw-font-semibold tw-px-8 tw-py-3 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mx-auto"
            >
              Go To Bottom
              <ArrowDown className="tw-w-4 tw-h-4" />
            </button>
          </div>

          {/* Table Container */}
          <div className="md:tw-bg-white md:tw-rounded-2xl tw-shadow-sm tw-p-0 md:tw-p-6 tw-overflow-x-auto">
            <div className="tw-min-w-full">
              <table className="tw-w-full tw-min-w-[700px] tw-border-collapse tw-text-center">
                <thead className="tw-bg-[#1e6ebe]">
                  <tr>
                    <th className="tw-border tw-p-2 tw-text-[10px] sm:tw-text-[11px] md:tw-text-[13px] tw-font-semibold !tw-text-white">
                      Date
                    </th>
                    {timeSlots.map((time, i) => (
                      <th
                        key={i}
                        className="tw-border tw-p-1 tw-text-[10px] sm:tw-text-[11px] md:tw-text-[13px] tw-font-semibold !tw-text-white"
                      >
                        {time}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {/* ✅ Static rows pehle render honge — instant load */}
                  {staticData.map((item, index) => (
                    <ChartRow
                      key={`static-${index}`}
                      item={item}
                      isJackpot={isJackpot}
                      isStarline={isStarline}
                    />
                  ))}

                  {/* ✅ API rows baad mein — skeleton jab tak API return na kare */}
                  {loading
                    ? Array.from({ length: 8 }).map((_, i) => (
                        <SkeletonRow
                          key={`skel-${i}`}
                          colCount={timeSlots.length + 1}
                        />
                      ))
                    : data.map((item, index) => (
                        <ChartRow
                          key={`dynamic-${index}`}
                          item={item}
                          isJackpot={isJackpot}
                          isStarline={isStarline}
                        />
                      ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Back Button */}
          <div className="tw-flex tw-justify-center tw-mt-3 md:tw-mt-8">
            <button
              onClick={() => navigate(-1)}
              className="tw-bg-[#1e6ebe] tw-text-white tw-font-semibold tw-px-8 tw-py-3 tw-rounded-lg tw-flex tw-items-center tw-gap-2"
            >
              <ArrowLeft className="tw-w-4 tw-h-4" />
              Back
            </button>
          </div>
        </div>
      </div>

      <Footer variant="mini" />
      <div id="scroll-down"></div>
    </>
  );
};

export default AllCharts;
